import { Fragment, useState } from "react";
import { Plus } from "lucide-react";
import type {
  EscalationCell,
  EscalationMatrixParams,
} from "@playbookai/widget-schemas";
import type { WidgetBodyProps } from "../types.js";
import { cn } from "../../lib/utils.js";
import { newWidgetItemId } from "../widget-item-id.js";
import {
  severities,
  levels,
  severityClass,
  toneClass,
} from "./presentation.js";
import { ResponseDetails } from "./ResponseDetails.js";

export function EscalationMatrixExpandedEditor({
  data,
  onChange,
  readOnly,
}: WidgetBodyProps<EscalationMatrixParams>) {
  const [selection, setSelection] = useState<
    Pick<EscalationCell, "severity" | "level">
  >(() => ({
    severity: data.cells[0]?.severity ?? "High",
    level: data.cells[0]?.level ?? "Owner",
  }));
  const [dirty, setDirty] = useState(false);
  const [revision, setRevision] = useState(0);
  const selected = data.cells.find(
    (cell) =>
      cell.severity === selection.severity && cell.level === selection.level,
  );
  const reset = () => {
    setDirty(false);
    setRevision((value) => value + 1);
  };
  return (
    <div className="@container flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
      <p className="text-sm text-pb-text-muted">
        {dirty
          ? "Save or cancel before selecting another cell."
          : "Select a cell to edit"}
      </p>
      <div className="grid shrink-0 gap-5 @3xl:min-h-0 @3xl:flex-1 @3xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="min-w-0 @3xl:overflow-auto">
          <div className="grid grid-cols-[48px_repeat(3,minmax(0,1fr))] gap-2">
            <div />
            {levels.map((level) => (
              <div
                key={level}
                className="pb-2 text-center text-xs font-medium text-pb-text-muted"
              >
                {level}
              </div>
            ))}
            {severities.map((severity) => (
              <Fragment key={severity}>
                <div
                  className={cn(
                    "flex items-center text-xs font-semibold",
                    severityClass[severity],
                  )}
                >
                  {severity}
                </div>
                {levels.map((level) => {
                  const cell = data.cells.find(
                    (item) =>
                      item.severity === severity && item.level === level,
                  );
                  const active =
                    selection.severity === severity &&
                    selection.level === level;
                  return (
                    // eslint-disable-next-line ds/no-hand-built -- kept: a toned 10px matrix cell with a teal ring when chosen; Card as="button" is a 14px corner with the glow selection, and no approved look row lists the change.
                    <button
                      key={level}
                      type="button"
                      aria-label={
                        severity +
                        " " +
                        level +
                        ": " +
                        (cell?.label ||
                          (cell ? "No action specified" : "Not set"))
                      }
                      aria-pressed={active}
                      disabled={dirty}
                      onClick={() => {
                        setSelection({ severity, level });
                        reset();
                      }}
                      className={cn(
                        "flex min-h-24 min-w-0 flex-col items-center justify-center gap-2 rounded-lg border p-2 text-center text-xs text-pb-text focus-ring @3xl:min-h-32",
                        toneClass[cell?.tone ?? "neutral"],
                        active && "ring-2 ring-pb-teal",
                        dirty && !active && "opacity-60",
                      )}
                    >
                      {cell ? (
                        <span className="whitespace-pre-wrap break-words">
                          {cell.label || "No action specified"}
                        </span>
                      ) : (
                        <>
                          <Plus
                            aria-hidden="true"
                            className="h-4 w-4 text-pb-text-muted"
                          />
                          <span className="text-pb-text-muted">Not set</span>
                        </>
                      )}
                    </button>
                  );
                })}
              </Fragment>
            ))}
          </div>
        </div>
        <div className="min-w-0 border-t border-pb-border pt-4 @3xl:overflow-auto @3xl:border-s @3xl:border-t-0 @3xl:ps-5 @3xl:pt-0">
          <ResponseDetails
            key={selection.severity + selection.level + revision}
            cell={selected}
            selection={selection}
            readOnly={!!readOnly}
            onDirty={() => setDirty(true)}
            onCancel={reset}
            onSave={(next) => {
              if (readOnly) return;
              const rest = data.cells.filter(
                (cell) =>
                  cell.severity !== selection.severity ||
                  cell.level !== selection.level,
              );
              /* PB-4923: carry the EXISTING cell's id through an edit, and
                 mint one only for a genuinely new cell. Re-minting on every
                 save would make each edit read as a delete plus an add, which
                 is exactly what the id exists to prevent. */
              onChange({
                ...data,
                cells: next
                  ? [
                      ...rest,
                      { id: selected?.id ?? newWidgetItemId("cell"), ...next },
                    ]
                  : rest,
              });
              reset();
            }}
          />
        </div>
      </div>
    </div>
  );
}
